import * as React from 'react';
import { Alert, AlertActionLink } from '@patternfly/react-core';
import { useIsUpdateAvailable } from '@app/utils/appUpdate';

/** The inset from the corner that everything floating down here uses. */
const EDGE_INSET = 30;

/** Breathing room between this box and whatever it is stacked on top of. */
const GAP = 15;

/** The feedback balloon, which owns the same corner whenever it is on screen. */
const FEEDBACK_BUTTON_ID = 'pf-feedback-button';

/**
 * How far off the bottom to sit: clear of the feedback balloon when there is one, and down
 * on the corner itself when there is not, so a disabled balloon does not leave a hole.
 *
 * Measured rather than assumed. The balloon's height is a product of its padding and icon
 * rather than a number anyone wrote down, and whether it renders at all is not something
 * this component can read — `FEEDBACK_ENABLED` exists in config but nothing consults it
 * yet, so the DOM is the only honest answer either way.
 */
const useBottomOffset = (isVisible: boolean): number => {
  const [bottom, setBottom] = React.useState(EDGE_INSET);

  // Layout effect, not effect: this runs before paint, so the box is never seen at the
  // wrong height first.
  React.useLayoutEffect(() => {
    if (!isVisible) return;

    const balloon = document.getElementById(FEEDBACK_BUTTON_ID);
    const height = balloon?.getBoundingClientRect().height ?? 0;

    setBottom(height > 0 ? EDGE_INSET + height + GAP : EDGE_INSET);
  }, [isVisible]);

  return bottom;
};

/**
 * The floating prompt that appears when a newer build has been deployed under a tab that is
 * still running the old one.
 *
 * Bottom left, stacked above the feedback balloon so the two share an edge instead of
 * covering each other. Deliberately not dismissible: until the page is reloaded this tab is
 * running code the server has replaced, and that does not stop being true because the
 * message was closed.
 *
 * Must be mounted OUTSIDE PatternFly's Page — it lives next to FeedbackBalloon in
 * `@app/index`, not in AppLayout. `.pf-v6-c-page__main-container` is a grid item carrying
 * `z-index: 100`, and z-index applies to grid items at `position: static`, so it opens a
 * stacking context that caps everything inside it below the sidebar's 200. Mounted in there
 * this box renders behind the sidebar, clipped, whatever z-index it asks for.
 */
export const NewVersionAlert: React.FunctionComponent = () => {
  const isUpdateAvailable = useIsUpdateAvailable();
  const bottom = useBottomOffset(isUpdateAvailable);

  if (!isUpdateAvailable) return null;

  return (
    <div
      id="cas-new-version-alert"
      style={{
        position: 'fixed',
        bottom: `${bottom}px`,
        left: `${EDGE_INSET}px`,
        // Below the feedback balloon's 9999 so it can never cover a control the user is
        // reaching for, and below the 10000 backdrop of a screenshot capture.
        zIndex: 9998,
        // On a phone 360px would run flush to the opposite edge, so keep the inset
        // symmetrical rather than letting the box touch the right of the screen.
        maxWidth: `min(360px, calc(100vw - ${EDGE_INSET * 2}px))`,
        boxShadow: 'var(--pf-t--global--box-shadow--lg)'
      }}
    >
      <Alert
        variant="info"
        title="A new version is available"
        actionLinks={
          // A full browser reload, not a router navigation: the whole bundle has to come
          // down again, and only the browser can fetch it.
          <AlertActionLink onClick={() => window.location.reload()}>Refresh now</AlertActionLink>
        }
      >
        Refresh the page to load it. Anything you have not saved will be lost.
      </Alert>
    </div>
  );
};
